/**
 * useVersionPoll — detect when a fresher deck deploy has shipped while
 * the operator's tab is still running the previous bundle.
 *
 * Both sides of the comparison are deck commit SHAs (same source —
 * Amplify's AWS_COMMIT_ID for that build) so a match means "you're on
 * the deck that's currently deployed" and a mismatch means "a newer
 * deck deploy has shipped, this tab is one or more builds behind".
 * Mixing the deck SHA against a shaka-api endpoint would never match
 * — they're two different repos with two different commit graphs.
 *
 * Wire format:
 *   - Build time: amplify.yml writes `dist/version.json` with the
 *     same AWS_COMMIT_ID it injects as VITE_BUILD_VERSION into the
 *     bundle. The file is served from the SPA's own origin at
 *     `/version.json`.
 *   - Runtime: the hook fetches `/version.json` (same origin → no
 *     CORS / no auth involved) every 60 s while the tab is visible,
 *     plus on focus and visibilitychange. A cache-busting query
 *     string defeats CDN caching of the JSON.
 *   - `'unknown'` on the bundle side (local dev without Amplify)
 *     short-circuits the loop so the banner never flashes there.
 *
 * The hook returns `{ serverVersion, isStale }`. The consumer (a
 * top-bar banner) decides what to render and when to refresh.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;
const VERSION_URL = '/version.json';
const BUILD_VERSION = (import.meta.env.VITE_BUILD_VERSION ?? '').trim();

interface VersionResponse {
  version?: string;
}

export interface VersionPollState {
  /** Last value returned by /version.json, or null while we haven't polled yet. */
  serverVersion: string | null;
  /**
   * True when both the build SHA and the server version are known and
   * they don't match — the running tab is one or more deploys behind.
   */
  isStale: boolean;
}

export function useVersionPoll(): VersionPollState {
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  // Hold the latest server version in a ref so the visibility handler
  // can read it without participating in the dependency array of
  // useEffect — the polling effect should only set up once.
  const latestRef = useRef<string | null>(null);
  latestRef.current = serverVersion;

  const fetchOnce = useCallback(async () => {
    try {
      // Cache-bust the JSON so CDN edge caches don't keep returning the
      // old SHA after a deploy. The browser HTTP cache also honours
      // `cache: 'no-store'`, but a fresh query string is the belt that
      // covers proxies between us and origin.
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
      });
      if (!res.ok) return;
      const body: VersionResponse = await res.json();
      const next = (body.version ?? '').trim();
      if (!next) return;
      if (next !== latestRef.current) {
        setServerVersion(next);
      }
    } catch {
      // Network blips are normal — silently ignore. The next interval
      // tick will retry.
    }
  }, []);

  useEffect(() => {
    // Skip the whole machinery when the bundle has no baked-in SHA
    // (local dev, ad-hoc preview builds). Without a known build SHA
    // there's nothing meaningful to compare against, so polling would
    // be pointless and the banner would never need to render.
    if (!BUILD_VERSION || BUILD_VERSION === 'unknown') {
      return;
    }

    // Initial fetch so the banner can light up within milliseconds of
    // the SPA mounting on a stale tab.
    fetchOnce();

    let intervalId: number | null = null;

    const startInterval = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(fetchOnce, POLL_INTERVAL_MS);
    };
    const stopInterval = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchOnce();
        startInterval();
      } else {
        stopInterval();
      }
    };
    const onFocus = () => {
      fetchOnce();
    };

    // Initial setup respects current tab state.
    if (document.visibilityState === 'visible') {
      startInterval();
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchOnce]);

  const isStale = Boolean(
    BUILD_VERSION
    && BUILD_VERSION !== 'unknown'
    && serverVersion
    && serverVersion !== BUILD_VERSION,
  );

  return { serverVersion, isStale };
}
